import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ThirdPartyRow {
  readonly thirdPartyId: string;
  readonly partyCode: string;
  readonly partyName: string;
  readonly partyType: string;
  readonly reviewStatus: string;
  readonly riskRating: string;
  readonly status: string;
  readonly nextReviewAt: string | null;
}

const TYPES = ['', 'VENDOR', 'SUPPLIER', 'PARTNER', 'CONTRACTOR', 'SERVICE_PROVIDER', 'PROCESSOR', 'SUB_PROCESSOR'];
const REVIEW_STATUSES = ['', 'PENDING', 'IN_PROGRESS', 'APPROVED', 'CONDITIONALLY_APPROVED', 'REJECTED', 'EXPIRED'];
const CREATE_TYPES = TYPES.filter(Boolean);
const REVIEW_DECISIONS = ['APPROVED', 'CONDITIONALLY_APPROVED', 'REJECTED', 'IN_PROGRESS'];

/**
 * M30 — Third-Party Security Dashboard (§19 / §31). Register of vendors,
 * processors and partners with their security-review status, computed risk
 * rating, lifecycle state and next-review date. Supports the review workflow
 * and time-boxed security exceptions for conditionally-approved parties —
 * registration, review and exception grant are wired to the guarded
 * /v1/security/third-party write API.
 */
export function M30ThirdPartySecurityPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ThirdPartyRow[]>([]);
  const [partyType, setPartyType] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('VENDOR');
  const [reviewDecision, setReviewDecision] = useState('APPROVED');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (partyType) params.set('partyType', partyType);
    if (reviewStatus) params.set('reviewStatus', reviewStatus);
    const qs = params.toString();
    apiRequest<{ data: ThirdPartyRow[] }>(`/v1/security/third-party${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, partyType, reviewStatus, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m30.thirdParty.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(tr('m30.thirdParty.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ThirdPartyRow }>(`/v1/security/third-party`, {
        method: 'POST',
        body: { partyName: name.trim(), partyType: newType },
        token,
      });
      setNotice(tr('m30.thirdParty.registered', { code: r.data.partyCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m30.thirdParty.verbRegister'), 'M30_THIRDPARTY_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const review = (p: ThirdPartyRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/third-party/${p.thirdPartyId}/review`, { method: 'POST', body: { reviewStatus: reviewDecision }, token }),
      tr('m30.thirdParty.reviewed', { code: p.partyCode }), tr('m30.thirdParty.verbReview'), 'M30_THIRDPARTY_MANAGE',
    );

  const grantException = (p: ThirdPartyRow): Promise<void> => {
    const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
    return runRowAction(
      () => apiRequest(`/v1/security/third-party/${p.thirdPartyId}/exception`, { method: 'POST', body: { exceptionReason: `Time-boxed exception for ${p.partyCode}`, exceptionExpiresAt: expires }, token }),
      tr('m30.thirdParty.exceptionGranted', { code: p.partyCode }), tr('m30.thirdParty.verbException'), 'M30_THIRDPARTY_MANAGE',
    );
  };

  return (
    <main aria-label="third-party-security">
      <h1>{tr('m30.thirdParty.title')}</h1>

      <section aria-label="create-third-party">
        <h2>{tr('m30.thirdParty.registerHeading')}</h2>
        <form aria-label="third-party-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m30.thirdParty.fieldName')}<input aria-label="field-party-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m30.thirdParty.fieldType')}
            <select aria-label="field-party-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m30.thirdParty.fieldReviewDecision')}
            <select aria-label="field-review-decision" value={reviewDecision} onChange={(e) => setReviewDecision(e.target.value)}>
              {REVIEW_DECISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-third-party" disabled={busy}>{busy ? tr('m30.thirdParty.submitRegistering') : tr('m30.thirdParty.submitRegister')}</button>
        </form>
        {actionError && <p role="alert" aria-label="third-party-error">{actionError}</p>}
        {notice && <p aria-label="third-party-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m30.thirdParty.filterType')}
          <select aria-label="filter-party-type" value={partyType} onChange={(e) => setPartyType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m30.thirdParty.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m30.thirdParty.filterReview')}
          <select aria-label="filter-review-status" value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)}>
            {REVIEW_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m30.thirdParty.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m30.thirdParty.loading')}</p> : (
        <>
          <p aria-label="third-party-count">{tr('m30.thirdParty.count', { count: rows.length })}</p>
          <table aria-label="third-party-table">
            <thead><tr><th>{tr('m30.thirdParty.thCode')}</th><th>{tr('m30.thirdParty.thName')}</th><th>{tr('m30.thirdParty.thType')}</th><th>{tr('m30.thirdParty.thReview')}</th><th>{tr('m30.thirdParty.thRisk')}</th><th>{tr('m30.thirdParty.thStatus')}</th><th>{tr('m30.thirdParty.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.thirdPartyId}>
                  <td>{p.partyCode}</td><td>{p.partyName}</td><td>{p.partyType}</td>
                  <td>{p.reviewStatus}</td><td>{p.riskRating}</td><td>{p.status}</td>
                  <td>
                    <button type="button" aria-label={`review-${p.partyCode}`} onClick={() => void review(p)}>{tr('m30.thirdParty.btnReview')}</button>
                    <button type="button" aria-label={`exception-${p.partyCode}`} onClick={() => void grantException(p)}>{tr('m30.thirdParty.btnException')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
